import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useForm } from "../hooks/useForm";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";
import { fetchCategories } from "../service/categoryService";
import { createSubscription } from "../service/subscriptionService";
import { getRestaurantId } from "../utils/JwtUtil";
import SubscriptionTabs from "../components/subscription/SubscriptionTabs";
import SubscriptionContent from "../components/subscription/SubscriptionContent";
import SubscriptionEditHeader from "../components/subscription/SubscriptionEditHeader";

export default function CreateSubscription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const restaurantId = getRestaurantId();
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState('basic');

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  // Use useMemo to ensure initialValues doesn't change on re-renders
  const initialValues = useMemo(() => ({
    nameEn: "",
    nameAr: "",
    descriptionEn: "",
    descriptionAr: "",
    expiryDays: "",
    mealsPerDay: "",
    pricePerDay: "",
    offerPercentage1Week: "0",
    offerPercentage2Weeks: "0",
    offerPercentage4Weeks: "0",
    offerPercentage8Weeks: "0",
    imageUrl: "",
    isActive: true,
    mealSpecifications: [],
  }), []);

  const { values, setField } = useForm(initialValues);

  // Track unsaved changes (for create, any change from initial empty state is considered unsaved)
  const { hasUnsavedChanges, markAsSaved } = useUnsavedChanges(values, initialValues);

  const addMealSpecification = () => {
    setField("mealSpecifications", [
      ...(values.mealSpecifications || []),
      { mealType: "BREAKFAST", numberOfMeals: 1, allowedCategoryIds: [] },
    ]);
  };

  const removeMealSpecification = (index) => {
    setField(
      "mealSpecifications",
      (values.mealSpecifications || []).filter((_, i) => i !== index)
    );
  };

  const updateMealSpecification = (index, field, value) => {
    const updated = (values.mealSpecifications || []).map((spec, i) =>
      i === index ? { ...spec, [field]: value } : spec
    );
    setField("mealSpecifications", updated);
  };

  const subMutation = useMutation({
    mutationFn: createSubscription,
    onSuccess: () => {
      markAsSaved(); // Mark changes as saved
      qc.invalidateQueries(["subscriptions", restaurantId]);
      navigate("/subscriptions");
    },
  });

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    subMutation.mutate({
      nameEn: values.nameEn,
      nameAr: values.nameAr,
      descriptionEn: values.descriptionEn,
      descriptionAr: values.descriptionAr,
      expiryDays: Number(values.expiryDays),
      mealsPerDay: Number(values.mealsPerDay),
      pricePerDay: Number(values.pricePerDay),
      offerPercentage1Week: Number(values.offerPercentage1Week),
      offerPercentage2Weeks: Number(values.offerPercentage2Weeks),
      offerPercentage4Weeks: Number(values.offerPercentage4Weeks),
      offerPercentage8Weeks: Number(values.offerPercentage8Weeks),
      imageUrl: values.imageUrl,
      isActive: values.isActive,
      restaurantId,
      mealSpecifications: values.mealSpecifications || [],
    });
  };

  const handleCancel = () => {
    navigate("/subscriptions");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Sticky Header */}
      <SubscriptionEditHeader
        onSave={handleSubmit}
        onCancel={handleCancel}
        saving={subMutation.isPending}
        hasUnsavedChanges={hasUnsavedChanges}
        title="createSubscription"
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Card */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          {/* Tabs Header */}
          <div className="bg-gradient-to-r from-white to-gray-50 px-8 pt-6 pb-0">
            <SubscriptionTabs 
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>

          {/* Form Content */}
          <form onSubmit={handleSubmit} className="px-8 pb-8">
            <div className="min-h-[600px] py-6">
              <SubscriptionContent
                activeTab={activeTab}
                values={values}
                setField={setField}
                categories={categories}
                addMealSpecification={addMealSpecification}
                removeMealSpecification={removeMealSpecification}
                updateMealSpecification={updateMealSpecification}
                t={t}
              />
            </div>
          </form>
        </div>

        {/* Footer Spacing */}
        <div className="h-8"></div>
      </div>
    </div>
  );
}